/**
 * Browser desktop notifications — requests permission on first use,
 * then shows native OS notifications for collaboration events.
 */

let pendingRequest: Promise<boolean> | null = null;

async function ensurePermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  // Always check live permission state (user may change in browser settings)
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  // Deduplicate concurrent permission requests
  if (pendingRequest) return pendingRequest;
  pendingRequest = Notification.requestPermission().then((result) => {
    pendingRequest = null;
    return result === "granted";
  });
  return pendingRequest;
}

export async function browserNotify(title: string, body: string, tag?: string): Promise<void> {
  const allowed = await ensurePermission();
  if (!allowed) return;
  new Notification(title, {
    body,
    icon: "/favicon.ico",
    tag: tag ?? "cad-plm",
  });
}

/**
 * Poll for new notifications and show desktop alerts.
 * Call once on app startup. Returns cleanup function.
 */
export function startNotificationPolling(intervalMs = 30000): () => void {
  let lastCheck = Date.now();

  const poll = async () => {
    try {
      const res = await fetch("/api/notifications", { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        for (const n of data) {
          const ts = n.created_at ? new Date(n.created_at).getTime() : NaN;
          if (!isNaN(ts) && ts > lastCheck) {
            browserNotify(n.title ?? "Notification", n.body ?? "", n.type ?? "cad-plm");
          }
        }
      }
      lastCheck = Date.now();
    } catch {
      // Network errors during polling are expected (offline, server restart)
    }
  };

  const id = setInterval(poll, intervalMs);
  poll();
  return () => clearInterval(id);
}
